import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Constants from 'constants/Constants';
import LoginStore from 'stores/LoginStore';
import {
  saveSEO,
  getMemberInfo,
  updateMemberInfo,
  getAvailableThemes,
  updateThemeForSite,
  saveObjects,
  updateGlobalLayout,
  getBlogPosts,
  updateHeaderSettings,
  uploadImage,
  // Super Admin APIs
  getSitesWithMembers,
  getMembersWithoutSites,
  createSiteForMember,
} from '../../services/CommonServices';
import EditorPage from '../EditorPage';
import ImageUploader from './ImageUploader';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import BlogManager from '../Admin/Blog/BlogManager'; // Import BlogManager
import { injectIntl } from 'react-intl';
import { useBuilder } from '../../contexts/BuilderContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useInstance } from '../../contexts/InstanceContext';
import FontSettingsModal from './FontSettingsModal';

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false, // Preserve original HTML formatting
  }
};

// Quill formats to allow (including class attribute)
const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'align',
  'link', 'image',
  'color', 'background',
  'indent',
  'class', 'id', 'style' // Allow class, id, and inline styles
];

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 400px;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.95);
  overflow-x: hidden;
  overflow-y: auto;
  transition: transform 0.3s ease;
  transform: translateX(${(props) => (props.isOpen ? '0' : '-100%')});
  padding-top: 60px;
  z-index: 1000;
  color: white;

  & label {
    display: block;
  }
  & input, & textarea, & select {
    width: 100%;
    font-size: 0.9rem;
    margin-bottom: 10px;
    background-color: #333;
    color: white;
    border: 1px solid #555;
    padding: 5px;
  }

  /* ReactQuill 에디터 스타일 수정 */
  & .ql-container {
    background-color: #fff;
    color: #333;
    min-height: 150px;
  }
  & .ql-editor {
    color: #333;
    min-height: 150px;
  }
  & .ql-editor.ql-blank::before {
    color: #999;
  }
  & .ql-toolbar {
    background-color: #f8f9fa;
    border-color: #ddd;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SidebarContent = styled.div`
  display: block;
  color: white;
  padding: 8px 16px;
  padding-bottom: 80px;
`;

const ToggleButton = styled.button`
  position: fixed;
  left: ${(props) => (props.isOpen ? '400px' : '0')};
  bottom: 20px;
  background-color: ${(props) => (props.isOpen ? '#dc3545' : '#333')};
  color: white;
  padding: 10px 20px;
  transition: all 0.3s ease;
  z-index: 1001;
  border: none;
  border-radius: ${(props) => (props.isOpen ? '0 5px 5px 0' : '0 5px 5px 0')};
  font-weight: 600;

  &:hover {
    background-color: ${(props) => (props.isOpen ? '#c82333' : '#444')};
  }

  @media (max-width: 768px) {
    left: ${(props) => (props.isOpen ? 'calc(100% - 80px)' : '0')};
  }
`;

// --- New Styled Components for Design Tab ---
const DesignSection = styled.div`
  background-color: #2a2a2a;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid #444;
`;

const SectionTitle = styled.h6`
  color: #ccc;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 15px;
  border-bottom: 1px solid #444;
  padding-bottom: 8px;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
`;

const ColorItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColorLabel = styled.span`
  font-size: 0.8rem;
  color: #aaa;
  margin-bottom: 5px;
`;

const ColorPreview = styled.div`
  width: 100%;
  height: 40px;
  background-color: ${props => props.color};
  border-radius: 4px;
  border: 1px solid #555;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover::after {
    content: '${props => props.color}';
    background: rgba(0,0,0,0.8);
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75rem;
  }
`;

// Color Editor Components
const ColorEditorItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  padding: 8px;
  background-color: #333;
  border-radius: 6px;
  border: 1px solid ${props => props.isCustom ? '#ffc107' : '#444'};
`;

const ColorPickerWrapper = styled.div`
  position: relative;

  input[type="color"] {
    width: 40px;
    height: 40px;
    padding: 0;
    border: 2px solid #555;
    border-radius: 6px;
    cursor: pointer;
    background: none;

    &::-webkit-color-swatch-wrapper {
      padding: 2px;
    }

    &::-webkit-color-swatch {
      border: none;
      border-radius: 4px;
    }
  }
`;

const ColorInputText = styled.input`
  flex: 1;
  padding: 8px 10px;
  background-color: #2a2a2a;
  color: #fff;
  border: 1px solid #555;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.85rem;

  &:focus {
    outline: none;
    border-color: #28a745;
  }
`;

const ColorLabelText = styled.span`
  min-width: 100px;
  font-size: 0.8rem;
  color: ${props => props.isCustom ? '#ffc107' : '#aaa'};
`;

const ResetButton = styled.button`
  padding: 4px 8px;
  background-color: transparent;
  color: #888;
  border: 1px solid #555;
  border-radius: 4px;
  font-size: 0.7rem;
  cursor: pointer;

  &:hover {
    background-color: #444;
    color: #fff;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  margin-bottom: 10px;
  border-bottom: 1px solid #444;
  cursor: pointer;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const CategoryTitle = styled.span`
  font-size: 0.85rem;
  color: #ccc;
  font-weight: 500;
`;

const ExpandIcon = styled.span`
  color: #888;
  font-size: 0.8rem;
  transform: rotate(${props => props.expanded ? '180deg' : '0deg'});
  transition: transform 0.2s ease;
`;

const ThemeSelect = styled.select`
  width: 100%;
  padding: 10px;
  background-color: #333;
  color: white;
  border: 1px solid #555;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 0.95rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #28a745;
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 10px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #218838;
  }
  
  &:disabled {
    background-color: #555;
    cursor: not-allowed;
  }
`;

const AdminSidebar = (props) => {
  const { site, page } = props;
  const {
    isEditMode,
    toggleEditMode,
    selectedComponent,
    deselectComponent,
    isSidebarOpen,
    toggleSidebar,
    openSidebar,
    updateSingleSiteSub,
    updateLiveSite,
  } = useBuilder();

  // Local Theme System
  const {
    currentThemeId: localThemeId,
    currentTheme: localTheme,
    baseTheme,
    setTheme: setLocalTheme,
    availableThemes: localAvailableThemes,
    isLoading: isThemeLoading,
    // Custom Colors
    customColors,
    setColor,
    resetColors,
    hasCustomColors,
    colors: themeColors,
    colorCategories,
    colorLabels,
    // Layout Mode
    layoutMode,
    setLayoutMode,
    availableLayoutModes,
  } = useTheme();

  const { memberId } = useInstance();

  // Color editor expanded categories state
  const [expandedCategories, setExpandedCategories] = useState({
    primary: true,
    headerFooter: true,
  });

  const [authInfo, setAuthInfo] = useState(LoginStore.authInfo);
  const [memberInfo, setMemberInfo] = useState({});

  // Super Admin: Site owner selector state
  const [sitesWithMembers, setSitesWithMembers] = useState([]);
  const [membersWithoutSites, setMembersWithoutSites] = useState([]);
  const [isCreatingSite, setIsCreatingSite] = useState(false);
  const [newSiteTitle, setNewSiteTitle] = useState('');
  const [selectedMemberForNewSite, setSelectedMemberForNewSite] = useState('');
  // For super admin: use localStorage if set, otherwise fall back to InstanceContext memberId
  // For regular users: always use InstanceContext memberId
  const [selectedMemberId, setSelectedMemberId] = useState(
    LoginStore.isSuperAdmin
      ? (localStorage.getItem('selectedMemberId') || memberId || process.env.REACT_APP_MEMBERS_ID)
      : memberId
  );

  // Sync selectedMemberId with InstanceContext memberId when it changes (for non-super admins)
  useEffect(() => {
    if (!LoginStore.isSuperAdmin && memberId) {
      setSelectedMemberId(memberId);
    }
  }, [memberId]);

  // Convert seo array to object for easier editing
  const [seoData, setSeoData] = useState({
    title: '',
    description: '',
    keywords: '',
    favicon: '',
    og_title: '',
    og_description: '',
    og_image: '',
    og_url: '',
    og_site_name: '',
    // Twitter Cards
    twitter_card: 'summary_large_image',
    twitter_title: '',
    twitter_description: '',
    twitter_image: '',
  });

  const [availableThemes, setAvailableThemes] = useState([]);
  const [currentThemeId, setCurrentThemeId] = useState(site.themeId);
  const [selectedThemeDetails, setSelectedThemeDetails] = useState(null);

  const [activeTab, setActiveTab] = useState('nav-dashboard');

  // Properties editing state
  const [editedObjects, setEditedObjects] = useState({});

  // HTML 소스 모드 토글 상태 (키별로 관리) - 기본값을 true로 설정하여 HTML 소스 모드로 시작
  const [htmlSourceMode, setHtmlSourceMode] = useState({});

  // Quill 초기 로드 완료 여부 추적 (초기 로드 시 onChange 무시용)
  const [quillInitialized, setQuillInitialized] = useState({});

  // Blog posts for BlogTabSection selection
  const [availableBlogPosts, setAvailableBlogPosts] = useState([]);

  // Font Settings Modal state
  const [isFontModalOpen, setIsFontModalOpen] = useState(false);

  // Debounce ref for live preview updates (prevents flickering)
  const debounceTimerRef = useRef(null);
  const DEBOUNCE_DELAY = 150; // ms

  // Auth Listener
  useEffect(() => {
    const onAuthChange = () => setAuthInfo(LoginStore.authInfo);
    LoginStore.addChangeListener(onAuthChange);
    return () => LoginStore.removeChangeListener(onAuthChange);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Super Admin: Load sites with members for selector dropdown
  // Runs when authInfo changes (after login) to ensure isSuperAdmin is available
  useEffect(() => {
    // Only load if user is super admin
    if (!LoginStore.isSuperAdmin) return;

    console.log('Super Admin detected, loading sites with members...');

    // Load sites with members
    getSitesWithMembers()
      .then((res) => {
        console.log('getSitesWithMembers response:', res.data);
        if (res.data.success) {
          setSitesWithMembers(res.data.sites);
        }
      })
      .catch((err) => console.error('Failed to load sites with members:', err));

    // Load members without sites
    getMembersWithoutSites()
      .then((res) => {
        console.log('getMembersWithoutSites response:', res.data);
        if (res.data.success) {
          setMembersWithoutSites(res.data.members);
        }
      })
      .catch((err) => console.error('Failed to load members without sites:', err));
  }, [authInfo]);

  // Initialize Data - Load member info based on selected member ID
  useEffect(() => {
    getMemberInfo(selectedMemberId)
      .then((res) => setMemberInfo(res.data.memberInfo))
      .catch((err) => console.error(err));

    getAvailableThemes()
      .then((res) => {
        setAvailableThemes(res.data.data);
        const current = res.data.data.find((t) => t.id === currentThemeId);
        setSelectedThemeDetails(current);
      })
      .catch((err) => console.error(err));
  }, [currentThemeId, selectedMemberId]);

  // Handle member change (selecting a site owner)
  const handleMemberChange = (e) => {
    const newMemberId = e.target.value;
    setSelectedMemberId(newMemberId);
    localStorage.setItem('selectedMemberId', newMemberId);
    // Reload the page to apply the new member
    window.location.reload();
  };

  // Super Admin: Create a new site for a member without a site
  const handleCreateSiteForMember = async () => {
    if (!selectedMemberForNewSite) {
      alert('멤버를 선택해주세요.');
      return;
    }

    setIsCreatingSite(true);
    try {
      const res = await createSiteForMember(
        selectedMemberForNewSite,
        newSiteTitle || '새 사이트',
        ''
      );
      if (res.data.success) {
        alert('사이트가 생성되었습니다!');
        // Reload to refresh the sites list
        window.location.reload();
      } else {
        alert('오류: ' + res.data.message);
      }
    } catch (err) {
      console.error(err);
      alert('사이트 생성 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsCreatingSite(false);
    }
  };

  // Sync SEO Data
  useEffect(() => {
    if (page.seo) {
      const newSeoData = {
        title: '',
        description: '',
        keywords: '',
        favicon: '',
        og_title: '',
        og_description: '',
        og_image: '',
        og_url: '',
        og_site_name: '',
        twitter_card: 'summary_large_image',
        twitter_title: '',
        twitter_description: '',
        twitter_image: '',
      };
      page.seo.forEach(item => {
        // Map 'og:title' -> 'og_title', 'twitter:card' -> 'twitter_card' etc.
        const key = item.name.replace('og:', 'og_').replace('twitter:', 'twitter_');
        if (newSeoData.hasOwnProperty(key)) {
          newSeoData[key] = item.content;
        } else if (newSeoData.hasOwnProperty(item.name)) {
          newSeoData[item.name] = item.content;
        }
      });

      setSeoData(prev => ({
        ...prev,
        ...newSeoData
      }));
    }
  }, [page.seo]);

  // Sync Selected Component Data - selectedComponent가 변경될 때만 실행
  useEffect(() => {
    if (selectedComponent) {
      // defaultObjects가 있으면 병합하여 모든 필드가 표시되도록 함
      const mergedObjects = selectedComponent.defaultObjects
        ? { ...selectedComponent.defaultObjects, ...selectedComponent.objects }
        : { ...selectedComponent.objects };
      setEditedObjects(mergedObjects);

      // htmlContent 필드는 기본적으로 HTML Source 모드로 시작 (Quill이 class를 제거하는 것 방지)
      const newHtmlSourceMode = {};
      Object.keys(mergedObjects).forEach(key => {
        if (key === 'htmlContent' || key === 'htmlContentKo' || key === 'htmlContentEn') {
          newHtmlSourceMode[key] = true; // HTML Source 모드로 시작
        }
      });
      setHtmlSourceMode(newHtmlSourceMode);
      setQuillInitialized({}); // Reset quill initialized state

      // 사이드바 열기 (openSidebar 사용하여 무한 루프 방지)
      openSidebar();
      setActiveTab('nav-properties');

      // Load blog posts if BlogTabSection is selected
      if (selectedComponent.componentTitle === 'BlogTabSection' && site?.id) {
        getBlogPosts(site.id, { limit: 50 })
          .then(res => {
            if (res.data.success) {
              const publishedPosts = res.data.data.posts.filter(p => p.is_published);
              setAvailableBlogPosts(publishedPosts);
            }
          })
          .catch(err => console.error('Error loading blog posts:', err));
      }
    }
  }, [selectedComponent, openSidebar, site?.id]);

  const handleInputChange = (e, key, isMemberInfo = false) => {
    const value = e.target.value;
    if (isMemberInfo) {
      setMemberInfo(prev => ({ ...prev, [key]: value }));
    } else {
      // For SEO
      setSeoData(prev => ({ ...prev, [key]: value }));
    }
  };
  
  // Special handler for SEO image uploads
  const handleSeoImageChange = (url, key) => {
      setSeoData(prev => ({ ...prev, [key]: url }));
  };

  const handleThemeChange = async () => {
    try {
      const res = await updateThemeForSite(site.id, currentThemeId);
      if (res.data.success) {
        alert('Theme updated successfully');
        window.location.reload();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSEOSave = async () => {
    try {
      // Pass seoData object directly (matching backend expectation)
      const res = await saveSEO(page.id, seoData);
      if (res.data.success) {
        alert('SEO settings saved successfully!');
        window.location.reload();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      alert('Error: ' + err);
    }
  };

  const handleMemberInfoSave = async () => {
    // Use selectedMemberId instead of env variable to support dynamic member switching
    console.log('Saving memberInfo for member ID:', selectedMemberId);
    console.log('memberInfo:', memberInfo);
    console.log('site_logo value:', memberInfo.site_logo);
    try {
      const res = await updateMemberInfo(selectedMemberId, memberInfo);
      if (res.data.success) {
        alert('Member info updated successfully');
        window.location.reload();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleThemeSelection = (e) => {
    const id = Number(e.target.value);
    setCurrentThemeId(id);
    const theme = availableThemes.find(t => t.id === id);
    setSelectedThemeDetails(theme);
  };

  // --- Properties Editor Logic ---
  // Debounced live preview update to prevent flickering
  const debouncedPreviewUpdate = useCallback((componentId, newObj) => {
    // Clear any pending update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    // Schedule new update
    debounceTimerRef.current = setTimeout(() => {
      updateSingleSiteSub(componentId, newObj);
    }, DEBOUNCE_DELAY);
  }, [updateSingleSiteSub]);

  const handleObjectChange = (keyPath, value) => {
    setEditedObjects((prev) => {
      const newObj = { ...prev };
      const keys = keyPath.split('.');
      let current = newObj;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      // 실시간 미리보기 업데이트
      if (selectedComponent) {
        // HeaderType01은 site 객체를 업데이트해야 함 (GlobalHeader에서 사용)
        if (selectedComponent.componentTitle === 'HeaderType01') {
          // editedObjects의 키를 site 객체의 키로 매핑
          const siteUpdates = {
            headerTitleMode: newObj.titleMode,
            headerTitleImage: newObj.titleImage,
            headerCompanyLogo: newObj.companyLogo,
            headerCompanyLogoLink: newObj.companyLogoLink,
            headerCompanyLogoPosition: newObj.companyLogoPosition,
            headerCompanyLogoMargin: newObj.companyLogoMargin,
            headerCompanyLogoWidth: newObj.companyLogoWidth,
            headerCompanyLogoHeight: newObj.companyLogoHeight,
            headerTitleImageMargin: newObj.titleImageMargin,
            headerTitleImageMaxHeight: newObj.titleImageMaxHeight,
            headerTitleImageMaxWidth: newObj.titleImageMaxWidth,
            headerBackgroundColor: newObj.backgroundColor,
            snsLinkBackgroundColor: newObj.snsLinkBackgroundColor,
            snsLinkTextColor: newObj.snsLinkTextColor,
          };
          updateLiveSite(siteUpdates);
        } else {
          // 일반 siteSub 컴포넌트는 기존 방식으로 업데이트
          debouncedPreviewUpdate(selectedComponent.id, newObj);
        }
      }

      return newObj;
    });
  };

  const handleObjectSave = async () => {
    if (!selectedComponent) return;
    try {
      let res;

      // HeaderType01은 site 설정으로 저장 (siteSub가 아님)
      if (selectedComponent.componentTitle === 'HeaderType01') {
        res = await updateHeaderSettings(selectedComponent.id, {
          headerTitleMode: editedObjects.titleMode,
          headerTitleImage: editedObjects.titleImage,
          // 회사 로고 관련 필드
          headerCompanyLogo: editedObjects.companyLogo,
          headerCompanyLogoLink: editedObjects.companyLogoLink,
          headerCompanyLogoPosition: editedObjects.companyLogoPosition,
          headerCompanyLogoMargin: editedObjects.companyLogoMargin,
          headerCompanyLogoWidth: editedObjects.companyLogoWidth,
          headerCompanyLogoHeight: editedObjects.companyLogoHeight,
          // titleImage 스타일 옵션
          headerTitleImageMargin: editedObjects.titleImageMargin,
          headerTitleImageMaxHeight: editedObjects.titleImageMaxHeight,
          headerTitleImageMaxWidth: editedObjects.titleImageMaxWidth,
          // 헤더 배경색
          headerBackgroundColor: editedObjects.backgroundColor,
          // SNS 링크 색상
          snsLinkBackgroundColor: editedObjects.snsLinkBackgroundColor,
          snsLinkTextColor: editedObjects.snsLinkTextColor,
        });
        if (res.data.success) {
          alert('Header settings saved successfully!');
          window.location.reload(); // Reload to reflect changes
        } else {
          alert('Error: ' + res.data.message);
        }
      } else {
        // 일반 siteSub 컴포넌트
        res = await saveObjects(selectedComponent.id, editedObjects);
        if (res.data.success) {
          // Update live preview without page reload
          updateSingleSiteSub(selectedComponent.id, editedObjects);
          alert('Saved successfully');
        } else {
          alert('Error: ' + res.data.message);
        }
      }
    } catch (err) {
      alert('Error: ' + err);
    }
  };

  // Global Layout Change Handler
  const handleGlobalLayoutChange = async (siteId, headerType, footerType) => {
    try {
      const res = await updateGlobalLayout(siteId, headerType, footerType);
      if (res.data.success) {
        alert('Global layout updated successfully!');
        window.location.reload();
      } else {
        throw new Error(res.data.message || 'Unknown error');
      }
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const renderObjectInput = (key, value, keyPath) => {
    // Special handling for titleMode (HeaderType01)
    if (key === 'titleMode') {
      return (
        <div key={keyPath} className="mb-3">
          <label style={{ color: '#ffc107' }}>타이틀 모드</label>
          <select
            value={value}
            onChange={(e) => handleObjectChange(keyPath, e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#333',
              color: 'white',
              border: '1px solid #555',
              borderRadius: '4px',
              fontSize: '0.9rem',
            }}
          >
            <option value="text">텍스트 (Text)</option>
            <option value="image">이미지 (Image)</option>
          </select>
          <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginTop: '5px' }}>
            텍스트: 타이틀과 서브타이틀을 텍스트로 표시 / 이미지: 로고 이미지로 표시
          </small>
        </div>
      );
    }

    // Special handling for companyLogoPosition (HeaderType01)
    if (key === 'companyLogoPosition') {
      return (
        <div key={keyPath} className="mb-3">
          <label style={{ color: '#ffc107' }}>회사 로고 위치</label>
          <select
            value={value}
            onChange={(e) => handleObjectChange(keyPath, e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#333',
              color: 'white',
              border: '1px solid #555',
              borderRadius: '4px',
              fontSize: '0.9rem',
            }}
          >
            <option value="top">타이틀 위쪽 (Top)</option>
            <option value="left">타이틀 왼쪽 (Left)</option>
            <option value="bottom">타이틀 아래쪽 (Bottom)</option>
          </select>
          <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginTop: '5px' }}>
            회사 로고가 타이틀/타이틀이미지 기준으로 어느 위치에 표시될지 선택하세요
          </small>
        </div>
      );
    }

    // Special handling for SNS link colors (컬러 피커)
    if (key === 'snsLinkBackgroundColor' || key === 'snsLinkTextColor') {
      const labelMap = {
        'snsLinkBackgroundColor': 'SNS 링크 배경색',
        'snsLinkTextColor': 'SNS 링크 글자색',
      };
      const descriptionMap = {
        'snsLinkBackgroundColor': '헤더 오른쪽 SNS 메뉴의 배경색',
        'snsLinkTextColor': '헤더 오른쪽 SNS 메뉴의 글자색',
      };
      return (
        <div key={keyPath} className="mb-3">
          <label style={{ color: '#ff9ff3' }}>{labelMap[key]}</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="color"
              value={value}
              onChange={(e) => handleObjectChange(keyPath, e.target.value)}
              style={{
                width: '50px',
                height: '35px',
                padding: '0',
                border: '1px solid #555',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            />
            <input
              type="text"
              value={value}
              onChange={(e) => handleObjectChange(keyPath, e.target.value)}
              placeholder="#ffffff"
              style={{
                flex: 1,
                padding: '8px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555',
                borderRadius: '4px',
                fontSize: '0.85rem',
              }}
            />
          </div>
          <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginTop: '5px' }}>
            {descriptionMap[key]}
          </small>
        </div>
      );
    }

    // Special handling for companyLogoLink (외부 에이전시 사이트 링크)
    if (key === 'companyLogoLink') {
      return (
        <div key={keyPath} className="mb-3">
          <label style={{ color: '#17a2b8' }}>에이전시 사이트 링크</label>
          <input
            type="url"
            value={value}
            onChange={(e) => handleObjectChange(keyPath, e.target.value)}
            placeholder="https://agency-website.com"
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#333',
              color: 'white',
              border: '1px solid #555',
              borderRadius: '4px',
              fontSize: '0.9rem',
            }}
          />
          <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginTop: '5px' }}>
            회사 로고 클릭 시 이동할 에이전시 웹사이트 주소 (새 탭에서 열림)
          </small>
        </div>
      );
    }

    // Special handling for size/margin fields with slider (companyLogo, titleImage)
    if (key === 'companyLogoMargin' || key === 'titleImageMargin' ||
        key === 'companyLogoWidth' || key === 'companyLogoHeight' ||
        key === 'titleImageMaxHeight' || key === 'titleImageMaxWidth') {
      const labelMap = {
        'companyLogoMargin': '회사 로고 여백',
        'companyLogoWidth': '회사 로고 너비',
        'companyLogoHeight': '회사 로고 높이',
        'titleImageMargin': '타이틀 이미지 여백',
        'titleImageMaxHeight': '타이틀 이미지 최대 높이',
        'titleImageMaxWidth': '타이틀 이미지 최대 너비',
      };
      const placeholderMap = {
        'companyLogoMargin': '0 0 15px 0 (상 우 하 좌)',
        'companyLogoWidth': 'auto 또는 150px',
        'companyLogoHeight': '50px 또는 auto',
        'titleImageMargin': '0 0 10px 0 (상 우 하 좌)',
        'titleImageMaxHeight': '120px',
        'titleImageMaxWidth': 'auto 또는 300px',
      };

      // 숫자만 추출 (예: "50px" -> 50)
      const numericMatch = value.match(/^(\d+)/);
      const numericValue = numericMatch ? parseInt(numericMatch[1]) : 0;
      const hasSlider = !value.includes(' ') && (value.endsWith('px') || value === 'auto');

      return (
        <div key={keyPath} className="mb-3">
          <label style={{ color: '#6ea8fe' }}>{labelMap[key] || key}</label>
          {hasSlider && (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
              <input
                type="range"
                min="0"
                max="200"
                value={numericValue}
                onChange={(e) => handleObjectChange(keyPath, `${e.target.value}px`)}
                style={{ flex: 1, cursor: 'pointer' }}
              />
              <span style={{ color: '#aaa', fontSize: '0.8rem', minWidth: '50px' }}>{numericValue}px</span>
            </div>
          )}
          <input
            type="text"
            value={value}
            onChange={(e) => handleObjectChange(keyPath, e.target.value)}
            placeholder={placeholderMap[key] || ''}
            style={{ fontSize: '0.85rem' }}
          />
          {key.includes('Margin') && (
            <small style={{ color: '#888', fontSize: '0.7rem', display: 'block', marginTop: '3px' }}>
              CSS 형식: 상 우 하 좌 (예: 10px 0 20px 0)
            </small>
          )}
        </div>
      );
    }

    // Special handling for selectedPosts array (BlogTabSection)
    if (key === 'selectedPosts' && Array.isArray(value)) {
      const selectedIds = value.map(id => parseInt(id));

      const handlePostToggle = (postId) => {
        const currentSelected = [...selectedIds];
        const index = currentSelected.indexOf(postId);

        if (index > -1) {
          // Remove if already selected
          currentSelected.splice(index, 1);
        } else if (currentSelected.length < 3) {
          // Add if less than 3 selected
          currentSelected.push(postId);
        } else {
          alert('최대 3개까지만 선택할 수 있습니다.');
          return;
        }

        handleObjectChange(keyPath, currentSelected);
      };

      return (
        <div key={keyPath} className="mb-3">
          <label style={{ color: '#ffc107', marginBottom: '10px', display: 'block' }}>
            표시할 블로그 글 선택 (최대 3개)
          </label>
          <div style={{
            maxHeight: '250px',
            overflowY: 'auto',
            backgroundColor: '#2a2a2a',
            borderRadius: '6px',
            padding: '10px',
            border: '1px solid #444'
          }}>
            {availableBlogPosts.length === 0 ? (
              <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>
                발행된 블로그 글이 없습니다.
              </p>
            ) : (
              availableBlogPosts.map(post => {
                const isSelected = selectedIds.includes(post.id);
                const selectionIndex = selectedIds.indexOf(post.id);
                return (
                  <div
                    key={post.id}
                    onClick={() => handlePostToggle(post.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px',
                      marginBottom: '6px',
                      backgroundColor: isSelected ? '#28a745' : '#333',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      border: isSelected ? '1px solid #28a745' : '1px solid #444'
                    }}
                  >
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: isSelected ? '#fff' : '#555',
                      color: isSelected ? '#28a745' : '#888',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '10px',
                      fontWeight: '600',
                      fontSize: '0.85rem'
                    }}>
                      {isSelected ? selectionIndex + 1 : ''}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '0.9rem',
                        fontWeight: isSelected ? '600' : '400',
                        color: isSelected ? '#fff' : '#ddd',
                        marginBottom: '2px'
                      }}>
                        {post.title}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: isSelected ? '#ccc' : '#888' }}>
                        {new Date(post.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {selectedIds.length > 0 && (
            <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#aaa' }}>
              선택됨: {selectedIds.length}/3
            </div>
          )}
        </div>
      );
    }

    if (typeof value === 'string') {
      // WYSIWYG 에디터 for htmlContent fields (supports Ko/En)
      if (key === 'htmlContent' || key === 'htmlContentKo' || key === 'htmlContentEn') {
        const labelMap = {
          'htmlContent': 'HTML 내용',
          'htmlContentKo': '한국어 내용 (Korean)',
          'htmlContentEn': '영어 내용 (English)'
        };
        // 기본값은 true (HTML Source 모드)
        const isSourceMode = htmlSourceMode[keyPath] !== false;

        const handleModeToggle = () => {
          if (isSourceMode) {
            // HTML Source → WYSIWYG 전환 시 경고
            const confirmed = window.confirm(
              '⚠️ WYSIWYG 모드로 전환하면 HTML의 class, id, style 속성이 제거될 수 있습니다.\n\n' +
              '복잡한 스타일이 적용된 HTML은 HTML Source 모드에서 편집하는 것을 권장합니다.\n\n' +
              '계속하시겠습니까?'
            );
            if (!confirmed) return;
          }
          setHtmlSourceMode(prev => ({ ...prev, [keyPath]: !isSourceMode }));
        };

        return (
          <div key={keyPath} className="mb-3">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ color: key === 'htmlContentEn' ? '#6ea8fe' : '#ffc107', margin: 0 }}>
                {labelMap[key] || key}
              </label>
              <button
                type="button"
                onClick={handleModeToggle}
                style={{
                  padding: '4px 10px',
                  fontSize: '0.75rem',
                  backgroundColor: isSourceMode ? '#28a745' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {isSourceMode ? 'WYSIWYG Mode' : 'HTML Source (권장)'}
              </button>
            </div>

            {isSourceMode && (
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#1a3a1a',
                borderRadius: '4px',
                marginBottom: '8px',
                fontSize: '0.75rem',
                color: '#8fdf8f'
              }}>
                ✓ HTML Source 모드: class, id, style 속성이 보존됩니다.
              </div>
            )}

            {isSourceMode ? (
              // HTML 소스 모드 (기본값, 안전)
              <textarea
                rows={12}
                value={value}
                onChange={(e) => handleObjectChange(keyPath, e.target.value)}
                style={{
                  width: '100%',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                  border: '1px solid #555',
                  borderRadius: '4px',
                  padding: '10px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              />
            ) : (
              // WYSIWYG 모드 (주의 필요)
              <>
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#3a1a1a',
                  borderRadius: '4px',
                  marginBottom: '8px',
                  fontSize: '0.75rem',
                  color: '#df8f8f'
                }}>
                  ⚠️ WYSIWYG 모드: class 속성이 제거될 수 있습니다. 간단한 편집에만 사용하세요.
                </div>
                <div style={{ backgroundColor: '#fff', borderRadius: '4px', color: '#333' }}>
                  <ReactQuill
                    value={value}
                    onChange={(content) => handleObjectChange(keyPath, content)}
                    theme="snow"
                    modules={quillModules}
                    formats={quillFormats}
                    preserveWhitespace={true}
                    style={{ minHeight: '200px', color: '#333' }}
                  />
                </div>
              </>
            )}
          </div>
        );
      }
      // fullWidth select dropdown
      if (key === 'fullWidth') {
        return (
          <div key={keyPath} className="mb-2">
            <label>{key} (전체 너비)</label>
            <select
              value={value}
              onChange={(e) => handleObjectChange(keyPath, e.target.value)}
            >
              <option value="true">전체 너비 (Full Width)</option>
              <option value="false">컨테이너 너비 (Container Width)</option>
            </select>
          </div>
        );
      }
      // imagePosition select dropdown
      if (key === 'imagePosition') {
        return (
          <div key={keyPath} className="mb-2">
            <label>{key}</label>
            <select
              value={value}
              onChange={(e) => handleObjectChange(keyPath, e.target.value)}
            >
              <option value="left">이미지 왼쪽 / 텍스트 오른쪽</option>
              <option value="right">텍스트 왼쪽 / 이미지 오른쪽</option>
            </select>
          </div>
        );
      }
      // customCSS - 코드 에디터 스타일 textarea
      if (key === 'customCSS') {
        return (
          <div key={keyPath} className="mb-3">
            <label style={{ color: '#ff79c6' }}>Custom CSS</label>
            <p style={{ fontSize: '0.75rem', color: '#888', margin: '5px 0' }}>
              사용 가능한 클래스: .its-container, .its-grid, .its-image-column, .its-image, .its-content-column
            </p>
            <textarea
              rows={8}
              value={value}
              onChange={(e) => handleObjectChange(keyPath, e.target.value)}
              placeholder={`.its-grid { max-width: 100%; }
.its-image { filter: brightness(0.9); }
.its-content-column { padding: 60px; }`}
              style={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                border: '1px solid #555',
                borderRadius: '4px',
                padding: '10px',
              }}
            />
          </div>
        );
      }
      // padding, margin 관련 필드 - 슬라이더 + 텍스트
      if (key === 'padding' || key === 'margin') {
        // 숫자만 추출 (예: "40px" -> 40)
        const numericValue = parseInt(value) || 0;
        return (
          <div key={keyPath} className="mb-2">
            <label>{key}</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="range"
                min="0"
                max="100"
                value={numericValue}
                onChange={(e) => handleObjectChange(keyPath, `${e.target.value}px`)}
                style={{ flex: 1, cursor: 'pointer' }}
              />
              <input
                type="text"
                value={value}
                onChange={(e) => handleObjectChange(keyPath, e.target.value)}
                placeholder="40px"
                style={{ width: '80px', textAlign: 'center' }}
              />
            </div>
          </div>
        );
      }
      // 색상 관련 필드 (backgroundColor, color 등)
      if (key.toLowerCase().includes('color')) {
        return (
          <div key={keyPath} className="mb-2">
            <label>{key}</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="color"
                value={value.startsWith('#') ? value : '#ffffff'}
                onChange={(e) => handleObjectChange(keyPath, e.target.value)}
                style={{ width: '50px', height: '35px', padding: '2px', cursor: 'pointer' }}
              />
              <input
                type="text"
                value={value}
                onChange={(e) => handleObjectChange(keyPath, e.target.value)}
                placeholder="#ffffff"
                style={{ flex: 1 }}
              />
            </div>
          </div>
        );
      }
      // 이미지 관련 키워드 체크 (pic, image, url, logo 등) - color 제외
      if (['pic', 'image', 'url', 'logo', 'background'].some(keyword =>
        key.toLowerCase().includes(keyword) && !key.toLowerCase().includes('color')
      )) {
        return (
          <div key={keyPath} className="mb-2">
            <label>{key}</label>
            <ImageUploader
              initialUrl={value}
              onUploadSuccess={(url) => handleObjectChange(keyPath, url)}
              folder="site-assets"
            />
            {/* URL 직접 입력 필드도 유지 (옵션) */}
            <input
              type="text"
              value={value}
              onChange={(e) => handleObjectChange(keyPath, e.target.value)}
              placeholder="Or enter URL directly"
              style={{marginTop: '5px', fontSize: '0.8rem'}}
            />
          </div>
        );
      } else {
        return (
          <div key={keyPath} className="mb-2">
            <label>{key}</label>
             <textarea
              rows={3}
              value={value}
              onChange={(e) => handleObjectChange(keyPath, e.target.value)}
            />
          </div>
        );
      }
    } else if (typeof value === 'number') {
      return (
        <div key={keyPath} className="mb-2">
          <label>{key}</label>
          <input
            type="number"
            value={value}
            onChange={(e) => handleObjectChange(keyPath, parseFloat(e.target.value))}
          />
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
       return (
         <div key={keyPath} className="mb-3 ps-3 border-start">
            <label className="text-info">{key}</label>
            {Object.keys(value).map((subKey) =>
              renderObjectInput(subKey, value[subKey], `${keyPath}.${subKey}`),
            )}
         </div>
       );
    }
  };


  const isLogin = authInfo && authInfo.status === Constants.AUTHED;
  if (!isLogin) return null;

  return (
    <>
      <SidebarContainer isOpen={isSidebarOpen}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #444' }}>
           <h5 className="m-0">Site Builder</h5>
        </div>

        <nav>
          <div className="nav nav-tabs nav-fill" id="nav-tab" role="tablist" style={{borderBottom: '1px solid #444'}}>
            {['Dashboard', 'Pages', 'Design', 'SEO', 'Blog', 'Properties'].map(tab => {
                const tabId = `nav-${tab.toLowerCase()}`;
                const isActive = activeTab === tabId;
                // Hide Properties tab if no component selected
                if (tab === 'Properties' && !selectedComponent) return null;

                return (
                    <button
                        key={tab}
                        className={`nav-link ${isActive ? 'active bg-dark text-white' : 'text-secondary'}`}
                        style={{border: 'none', borderRadius: 0}}
                        onClick={() => setActiveTab(tabId)}
                        type="button"
                    >
                        {tab}
                    </button>
                );
            })}
          </div>
        </nav>

        <div className="tab-content">
           {/* Dashboard Tab */}
           <div className={`tab-pane fade ${activeTab === 'nav-dashboard' ? 'show active' : ''}`}>
             <SidebarContent>
                {/* Super Admin Only: Site Owner Selector */}
                {LoginStore.isSuperAdmin && (
                  <DesignSection style={{ marginTop: '15px', marginBottom: '20px', borderColor: '#ffc107' }}>
                    <SectionTitle style={{ color: '#ffc107' }}>
                      🔐 사이트 소유자 선택 (Super Admin)
                    </SectionTitle>
                    <p style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '15px' }}>
                      관리할 사이트의 소유자를 선택하세요. 변경 시 페이지가 새로고침됩니다.
                    </p>

                    {/* Sites with Members Dropdown */}
                    <select
                      value={selectedMemberId}
                      onChange={handleMemberChange}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#333',
                        color: 'white',
                        border: '1px solid #555',
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        cursor: 'pointer',
                      }}
                    >
                      {sitesWithMembers.map((site) => (
                        <option key={site.site_id} value={site.member_id}>
                          [{site.site_title || '제목 없음'}] {site.member_name || '이름 없음'} ({site.member_ename || site.member_email})
                          {!site.member_active && ' [비활성]'}
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginTop: '8px' }}>
                      현재 선택: Member ID {selectedMemberId} | 총 {sitesWithMembers.length}개 사이트
                    </small>

                    {/* Create Site for Members Without Sites */}
                    {membersWithoutSites.length > 0 && (
                      <div style={{
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#2a2a1a',
                        borderRadius: '8px',
                        border: '1px dashed #888'
                      }}>
                        <h6 style={{ color: '#ffc107', marginBottom: '12px', fontSize: '0.85rem' }}>
                          ➕ 새 사이트 생성 ({membersWithoutSites.length}명 대기 중)
                        </h6>

                        <label style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '5px' }}>
                          멤버 선택
                        </label>
                        <select
                          value={selectedMemberForNewSite}
                          onChange={(e) => setSelectedMemberForNewSite(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#333',
                            color: 'white',
                            border: '1px solid #555',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                            marginBottom: '10px',
                          }}
                        >
                          <option value="">-- 멤버를 선택하세요 --</option>
                          {membersWithoutSites.map((member) => (
                            <option key={member.id} value={member.id}>
                              {member.name} ({member.e_name || member.email})
                            </option>
                          ))}
                        </select>

                        <label style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '5px' }}>
                          사이트 제목 (선택사항)
                        </label>
                        <input
                          type="text"
                          value={newSiteTitle}
                          onChange={(e) => setNewSiteTitle(e.target.value)}
                          placeholder="새 사이트 제목"
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#333',
                            color: 'white',
                            border: '1px solid #555',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                            marginBottom: '10px',
                          }}
                        />

                        <button
                          onClick={handleCreateSiteForMember}
                          disabled={isCreatingSite || !selectedMemberForNewSite}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: isCreatingSite ? '#555' : '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isCreatingSite ? 'not-allowed' : 'pointer',
                            fontWeight: '600',
                          }}
                        >
                          {isCreatingSite ? '생성 중...' : '사이트 생성'}
                        </button>
                      </div>
                    )}
                  </DesignSection>
                )}

                <h6 className="mt-3 text-warning">Profile Settings</h6>
                <div className="mb-2">
                    <label>Name</label>
                    <input type="text" value={memberInfo.name || ''} onChange={(e) => handleInputChange(e, 'name', true)} />
                </div>
                 {/* Simplified for brevity, add other fields as needed */}
                 <div className="mb-2">
                    <label>Email</label>
                    <input type="text" value={memberInfo.email || ''} onChange={(e) => handleInputChange(e, 'email', true)} />
                </div>
                 <div className="mb-2">
                    <label>Company Name (회사 이름)</label>
                    <input type="text" value={memberInfo.company_name || ''} onChange={(e) => handleInputChange(e, 'company_name', true)} placeholder="ABC 부동산" />
                </div>
                 <div className="mb-2">
                    <label>Phone (개인 전화번호)</label>
                    <input type="text" value={memberInfo.phone || ''} onChange={(e) => handleInputChange(e, 'phone', true)} />
                </div>
                 <div className="mb-2">
                    <label>Company Phone (회사 전화번호)</label>
                    <input type="text" value={memberInfo.company_phone || ''} onChange={(e) => handleInputChange(e, 'company_phone', true)} placeholder="02-1234-5678" />
                </div>
                 <div className="mb-2">
                    <label>Company Address (회사 주소)</label>
                    <input type="text" value={memberInfo.company_address || ''} onChange={(e) => handleInputChange(e, 'company_address', true)} placeholder="서울시 강남구 테헤란로 123" />
                </div>

                 <div className="mb-3">
                    <label>Site Logo (Footer용)</label>
                    <ImageUploader
                      initialUrl={memberInfo.site_logo || ''}
                      onUploadSuccess={(url) => {
                        console.log('Logo upload success, URL:', url);
                        setMemberInfo(prev => ({ ...prev, site_logo: url }));
                      }}
                      folder="site-logos"
                    />
                    {memberInfo.site_logo && (
                      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#333', borderRadius: '4px' }}>
                        <small style={{ color: '#888', display: 'block', marginBottom: '5px' }}>미리보기:</small>
                        <img
                          src={memberInfo.site_logo}
                          alt="Site Logo"
                          style={{ maxHeight: '40px', filter: 'brightness(0) invert(1)' }}
                        />
                      </div>
                    )}
                </div>

                 <button className="btn btn-primary w-100 mt-2" onClick={handleMemberInfoSave}>
                  Save Profile
                </button>
             </SidebarContent>
           </div>

          {/* Pages Tab */}
          <div className={`tab-pane fade ${activeTab === 'nav-pages' ? 'show active' : ''}`}>
             <SidebarContent>
              <EditorPage
                siteSubs={props.page.siteSubs}
                pageId={page.id}
                site={site}
                onGlobalLayoutSave={handleGlobalLayoutChange}
              />
            </SidebarContent>
          </div>

          {/* Design Tab */}
          <div className={`tab-pane fade ${activeTab === 'nav-design' ? 'show active' : ''}`}>
             <SidebarContent>
              {/* Layout Mode Switcher */}
              <DesignSection>
                <SectionTitle>Layout Mode</SectionTitle>
                <p style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '15px' }}>
                  레이아웃 스타일을 선택하세요.
                </p>

                <div style={{ display: 'flex', gap: '10px' }}>
                  {availableLayoutModes.map((mode) => (
                    <div
                      key={mode.id}
                      onClick={() => setLayoutMode(mode.id)}
                      style={{
                        flex: 1,
                        padding: '15px 12px',
                        backgroundColor: layoutMode === mode.id ? '#1a3a1a' : '#333',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: layoutMode === mode.id ? '2px solid #28a745' : '2px solid transparent',
                        transition: 'all 0.2s ease',
                        textAlign: 'center',
                      }}
                    >
                      {/* Mode Icon */}
                      <div style={{
                        fontSize: '1.5rem',
                        marginBottom: '8px',
                      }}>
                        {mode.id === 'bordered' ? (
                          <span style={{
                            display: 'inline-block',
                            width: '30px',
                            height: '20px',
                            border: '2px solid #888',
                            borderRadius: '4px',
                            boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                          }} />
                        ) : (
                          <span style={{
                            display: 'inline-block',
                            width: '40px',
                            height: '20px',
                            backgroundColor: '#555',
                            borderRadius: '0',
                          }} />
                        )}
                      </div>

                      {/* Mode Name */}
                      <div style={{
                        fontWeight: layoutMode === mode.id ? '600' : '400',
                        color: layoutMode === mode.id ? '#28a745' : '#fff',
                        fontSize: '0.9rem',
                        marginBottom: '4px',
                      }}>
                        {mode.name}
                      </div>

                      {/* Mode Description */}
                      <div style={{ fontSize: '0.7rem', color: '#888' }}>
                        {mode.description}
                      </div>

                      {/* Active Badge */}
                      {layoutMode === mode.id && (
                        <div style={{
                          marginTop: '8px',
                          backgroundColor: '#28a745',
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: '600',
                          display: 'inline-block',
                        }}>
                          Active
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </DesignSection>

              {/* Font Settings Section */}
              <DesignSection>
                <SectionTitle>폰트 설정</SectionTitle>
                <p style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '15px' }}>
                  제목과 본문의 폰트, 자간, 줄 간격 등을 설정하세요.
                </p>

                <button
                  onClick={() => setIsFontModalOpen(true)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#333';
                    e.target.style.borderColor = '#ffc107';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#2a2a2a';
                    e.target.style.borderColor = '#444';
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>🔤</span>
                    폰트 설정 열기
                  </span>
                  <span style={{ color: '#888' }}>→</span>
                </button>
              </DesignSection>

              {/* Theme Preset Selection */}
              <DesignSection>
                <SectionTitle>테마 프리셋</SectionTitle>
                <p style={{ fontSize: '0.8rem', color: '#aaa', marginBottom: '15px' }}>
                  기본 테마를 선택한 후 아래에서 색상을 커스터마이즈하세요.
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '8px',
                  marginBottom: '15px',
                  maxHeight: '320px',
                  overflowY: 'auto',
                  padding: '4px',
                }}>
                  {localAvailableThemes.map((theme) => {
                    const isSelected = localThemeId === theme.id;
                    return (
                      <div
                        key={theme.id}
                        onClick={() => !isThemeLoading && setLocalTheme(theme.id)}
                        style={{
                          padding: '8px 6px',
                          backgroundColor: isSelected ? '#1a3a1a' : '#2a2a2a',
                          borderRadius: '6px',
                          cursor: isThemeLoading ? 'not-allowed' : 'pointer',
                          border: isSelected ? '2px solid #28a745' : '1px solid #444',
                          transition: 'all 0.15s ease',
                          textAlign: 'center',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#383838';
                            e.currentTarget.style.borderColor = '#666';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#2a2a2a';
                            e.currentTarget.style.borderColor = '#444';
                          }
                        }}
                      >
                        {/* Color Preview - Compact */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '3px',
                          marginBottom: '6px'
                        }}>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '3px',
                            backgroundColor: theme.previewColor,
                            border: '1px solid rgba(255,255,255,0.2)'
                          }} />
                          <div style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '3px',
                            backgroundColor: theme.secondaryColor,
                            border: '1px solid rgba(255,255,255,0.2)'
                          }} />
                        </div>
                        {/* Theme Name - Compact */}
                        <div style={{
                          fontSize: '0.7rem',
                          color: isSelected ? '#28a745' : '#ccc',
                          fontWeight: isSelected ? '600' : '400',
                          lineHeight: '1.2',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {theme.name}
                        </div>
                        {/* Selected indicator */}
                        {isSelected && (
                          <div style={{
                            marginTop: '4px',
                            fontSize: '0.6rem',
                            color: '#28a745',
                          }}>
                            ✓
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {hasCustomColors && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: '#3a3a1a', borderRadius: '6px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '0.8rem', color: '#ffc107' }}>
                      ✏️ 커스텀 색상이 적용됨 ({Object.keys(customColors).length}개)
                    </span>
                    <ResetButton onClick={resetColors}>
                      초기화
                    </ResetButton>
                  </div>
                )}
              </DesignSection>

              {/* Color Editor */}
              <DesignSection>
                <SectionTitle>색상 커스터마이즈</SectionTitle>
                <p style={{ fontSize: '0.75rem', color: '#888', marginBottom: '15px' }}>
                  각 색상을 클릭하여 원하는 색상으로 변경하세요. 변경 사항은 즉시 적용됩니다.
                </p>

                {Object.entries(colorCategories).map(([categoryKey, category]) => (
                  <div key={categoryKey} style={{ marginBottom: '5px' }}>
                    <CategoryHeader
                      onClick={() => setExpandedCategories(prev => ({
                        ...prev,
                        [categoryKey]: !prev[categoryKey]
                      }))}
                    >
                      <CategoryTitle>{category.labelKo}</CategoryTitle>
                      <ExpandIcon expanded={expandedCategories[categoryKey]}>▼</ExpandIcon>
                    </CategoryHeader>

                    {expandedCategories[categoryKey] && (
                      <div style={{ paddingLeft: '5px' }}>
                        {category.colors.map((colorKey) => {
                          const currentValue = themeColors[colorKey];
                          const isCustom = customColors.hasOwnProperty(colorKey);
                          const defaultValue = baseTheme?.colors?.[colorKey];

                          return (
                            <ColorEditorItem key={colorKey} isCustom={isCustom}>
                              <ColorPickerWrapper>
                                <input
                                  type="color"
                                  value={currentValue?.startsWith('#') ? currentValue : '#ffffff'}
                                  onChange={(e) => setColor(colorKey, e.target.value)}
                                />
                              </ColorPickerWrapper>
                              <div style={{ flex: 1 }}>
                                <ColorLabelText isCustom={isCustom}>
                                  {colorLabels[colorKey] || colorKey}
                                  {isCustom && ' ✏️'}
                                </ColorLabelText>
                                <ColorInputText
                                  type="text"
                                  value={currentValue || ''}
                                  onChange={(e) => setColor(colorKey, e.target.value)}
                                  placeholder="#000000"
                                />
                              </div>
                              {isCustom && (
                                <ResetButton
                                  onClick={() => {
                                    // Remove this color from customColors
                                    const newCustomColors = { ...customColors };
                                    delete newCustomColors[colorKey];
                                    // Update via setColor with default value
                                    if (defaultValue) {
                                      setColor(colorKey, defaultValue);
                                      // Then reset it
                                      setTimeout(() => {
                                        const stored = JSON.parse(localStorage.getItem('bubanc-custom-colors') || '{}');
                                        delete stored[colorKey];
                                        localStorage.setItem('bubanc-custom-colors', JSON.stringify(stored));
                                        window.location.reload();
                                      }, 100);
                                    }
                                  }}
                                  title="기본값으로 복원"
                                >
                                  ↺
                                </ResetButton>
                              )}
                            </ColorEditorItem>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </DesignSection>

              {/* Backend Theme System - Legacy */}
              <DesignSection>
                <SectionTitle>Backend Theme (Legacy)</SectionTitle>
                <p style={{ fontSize: '0.75rem', color: '#888', marginBottom: '10px' }}>
                  서버에 저장된 테마 설정입니다.
                </p>
                <ThemeSelect
                  onChange={handleThemeSelection}
                  value={currentThemeId}
                >
                  {availableThemes.map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      {theme.themeName}
                    </option>
                  ))}
                </ThemeSelect>

                {selectedThemeDetails && (
                  <>
                    <ColorGrid>
                      <ColorItem>
                        <ColorLabel>Primary</ColorLabel>
                        <ColorPreview color={`#${selectedThemeDetails.colors.primaryColor}`} />
                      </ColorItem>
                      <ColorItem>
                        <ColorLabel>Secondary</ColorLabel>
                        <ColorPreview color={`#${selectedThemeDetails.colors.secondaryColor}`} />
                      </ColorItem>
                      <ColorItem>
                        <ColorLabel>Title</ColorLabel>
                        <ColorPreview color={`#${selectedThemeDetails.colors.titleColor}`} />
                      </ColorItem>
                      <ColorItem>
                        <ColorLabel>Body</ColorLabel>
                        <ColorPreview color={`#${selectedThemeDetails.colors.bodyColor}`} />
                      </ColorItem>
                    </ColorGrid>
                  </>
                )}

                <ApplyButton onClick={handleThemeChange} style={{ marginTop: '15px' }}>
                  Apply Backend Theme
                </ApplyButton>
              </DesignSection>
            </SidebarContent>
          </div>

          {/* SEO Tab */}
          <div className={`tab-pane fade ${activeTab === 'nav-seo' ? 'show active' : ''}`}>
             <SidebarContent>
                {/* Basic SEO Section */}
                <DesignSection>
                  <SectionTitle>기본 SEO</SectionTitle>

                  <div className="mb-3">
                    <label>
                      페이지 제목 (Title)
                      <span style={{ fontSize: '0.75rem', color: (seoData.title?.length || 0) > 60 ? '#dc3545' : '#6c757d', marginLeft: '8px' }}>
                        {seoData.title?.length || 0}/60
                      </span>
                    </label>
                    <input
                      type="text"
                      value={seoData.title || ''}
                      onChange={(e) => handleInputChange(e, 'title')}
                      placeholder="사이트 제목을 입력하세요"
                    />
                    <small style={{ color: '#888', fontSize: '0.75rem' }}>검색 결과에 표시되는 제목 (권장 60자 이하)</small>
                  </div>

                  <div className="mb-3">
                    <label>
                      설명 (Description)
                      <span style={{ fontSize: '0.75rem', color: (seoData.description?.length || 0) > 160 ? '#dc3545' : '#6c757d', marginLeft: '8px' }}>
                        {seoData.description?.length || 0}/160
                      </span>
                    </label>
                    <textarea
                      rows={3}
                      value={seoData.description || ''}
                      onChange={(e) => handleInputChange(e, 'description')}
                      placeholder="사이트에 대한 간략한 설명을 입력하세요"
                    />
                    <small style={{ color: '#888', fontSize: '0.75rem' }}>검색 결과에 표시되는 설명 (권장 160자 이하)</small>
                  </div>

                  <div className="mb-3">
                    <label>키워드 (Keywords)</label>
                    <input
                      type="text"
                      value={seoData.keywords || ''}
                      onChange={(e) => handleInputChange(e, 'keywords')}
                      placeholder="부동산, 매물, 강남 (쉼표로 구분)"
                    />
                    <small style={{ color: '#888', fontSize: '0.75rem' }}>검색에 도움이 되는 키워드 (쉼표로 구분)</small>
                  </div>

                  <div className="mb-2">
                    <label>파비콘 (Favicon)</label>
                    <ImageUploader
                      initialUrl={seoData.favicon || ''}
                      onUploadSuccess={(url) => handleSeoImageChange(url, 'favicon')}
                      folder="site-assets"
                    />
                    <input
                      type="text"
                      value={seoData.favicon || ''}
                      onChange={(e) => handleInputChange(e, 'favicon')}
                      placeholder="또는 URL 직접 입력"
                      style={{ marginTop: '5px', fontSize: '0.85rem' }}
                    />
                  </div>
                </DesignSection>

                {/* Open Graph Section */}
                <DesignSection>
                  <SectionTitle>Open Graph (소셜 미디어)</SectionTitle>
                  <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginBottom: '15px' }}>
                    Facebook, LinkedIn 등에서 공유될 때 표시되는 정보
                  </small>

                  <div className="mb-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ margin: 0 }}>OG 제목</label>
                      <button
                        type="button"
                        onClick={() => setSeoData(prev => ({ ...prev, og_title: prev.title || '' }))}
                        style={{
                          padding: '2px 8px',
                          fontSize: '0.7rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        기본 제목에서 복사
                      </button>
                    </div>
                    <input
                      type="text"
                      value={seoData.og_title || ''}
                      onChange={(e) => handleInputChange(e, 'og_title')}
                      placeholder="소셜 미디어용 제목"
                      style={{ marginTop: '5px' }}
                    />
                  </div>

                  <div className="mb-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ margin: 0 }}>OG 설명</label>
                      <button
                        type="button"
                        onClick={() => setSeoData(prev => ({ ...prev, og_description: prev.description || '' }))}
                        style={{
                          padding: '2px 8px',
                          fontSize: '0.7rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        기본 설명에서 복사
                      </button>
                    </div>
                    <textarea
                      rows={2}
                      value={seoData.og_description || ''}
                      onChange={(e) => handleInputChange(e, 'og_description')}
                      placeholder="소셜 미디어용 설명"
                      style={{ marginTop: '5px' }}
                    />
                  </div>

                  <div className="mb-3">
                    <label>OG 이미지</label>
                    <ImageUploader
                      initialUrl={seoData.og_image || ''}
                      onUploadSuccess={(url) => handleSeoImageChange(url, 'og_image')}
                      folder="site-assets"
                    />
                    <input
                      type="text"
                      value={seoData.og_image || ''}
                      onChange={(e) => handleInputChange(e, 'og_image')}
                      placeholder="또는 URL 직접 입력"
                      style={{ marginTop: '5px', fontSize: '0.85rem' }}
                    />
                    <small style={{ color: '#888', fontSize: '0.75rem' }}>권장 크기: 1200x630px</small>
                  </div>

                  <div className="mb-3">
                    <label>OG URL</label>
                    <input
                      type="text"
                      value={seoData.og_url || ''}
                      onChange={(e) => handleInputChange(e, 'og_url')}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="mb-2">
                    <label>사이트 이름 (Site Name)</label>
                    <input
                      type="text"
                      value={seoData.og_site_name || ''}
                      onChange={(e) => handleInputChange(e, 'og_site_name')}
                      placeholder="내 부동산 사이트"
                    />
                  </div>
                </DesignSection>

                {/* Twitter Cards Section */}
                <DesignSection>
                  <SectionTitle>Twitter Cards</SectionTitle>
                  <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginBottom: '15px' }}>
                    Twitter(X)에서 공유될 때 표시되는 정보
                  </small>

                  <div className="mb-3">
                    <label>카드 타입</label>
                    <select
                      value={seoData.twitter_card || 'summary_large_image'}
                      onChange={(e) => handleInputChange(e, 'twitter_card')}
                    >
                      <option value="summary">Summary (작은 이미지)</option>
                      <option value="summary_large_image">Summary Large Image (큰 이미지)</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ margin: 0 }}>Twitter 제목</label>
                      <button
                        type="button"
                        onClick={() => setSeoData(prev => ({ ...prev, twitter_title: prev.og_title || prev.title || '' }))}
                        style={{
                          padding: '2px 8px',
                          fontSize: '0.7rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        OG/기본에서 복사
                      </button>
                    </div>
                    <input
                      type="text"
                      value={seoData.twitter_title || ''}
                      onChange={(e) => handleInputChange(e, 'twitter_title')}
                      placeholder="Twitter용 제목"
                      style={{ marginTop: '5px' }}
                    />
                  </div>

                  <div className="mb-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ margin: 0 }}>Twitter 설명</label>
                      <button
                        type="button"
                        onClick={() => setSeoData(prev => ({ ...prev, twitter_description: prev.og_description || prev.description || '' }))}
                        style={{
                          padding: '2px 8px',
                          fontSize: '0.7rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        OG/기본에서 복사
                      </button>
                    </div>
                    <textarea
                      rows={2}
                      value={seoData.twitter_description || ''}
                      onChange={(e) => handleInputChange(e, 'twitter_description')}
                      placeholder="Twitter용 설명"
                      style={{ marginTop: '5px' }}
                    />
                  </div>

                  <div className="mb-2">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <label style={{ margin: 0 }}>Twitter 이미지</label>
                      <button
                        type="button"
                        onClick={() => setSeoData(prev => ({ ...prev, twitter_image: prev.og_image || '' }))}
                        style={{
                          padding: '2px 8px',
                          fontSize: '0.7rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        OG 이미지에서 복사
                      </button>
                    </div>
                    <ImageUploader
                      initialUrl={seoData.twitter_image || ''}
                      onUploadSuccess={(url) => handleSeoImageChange(url, 'twitter_image')}
                      folder="site-assets"
                    />
                    <input
                      type="text"
                      value={seoData.twitter_image || ''}
                      onChange={(e) => handleInputChange(e, 'twitter_image')}
                      placeholder="또는 URL 직접 입력"
                      style={{ marginTop: '5px', fontSize: '0.85rem' }}
                    />
                  </div>
                </DesignSection>

                <ApplyButton onClick={handleSEOSave}>
                  SEO 설정 저장
                </ApplyButton>
             </SidebarContent>
          </div>

          {/* Blog Tab */}
          <div className={`tab-pane fade ${activeTab === 'nav-blog' ? 'show active' : ''}`}>
             <SidebarContent>
                <BlogManager siteId={site.id} />
             </SidebarContent>
          </div>

          {/* Properties Tab (Dynamic) */}
           <div className={`tab-pane fade ${activeTab === 'nav-properties' ? 'show active' : ''}`}>
              <SidebarContent>
                 <h6 className="mt-3 text-warning">
                    Edit: {selectedComponent ? selectedComponent.componentTitle || 'Component' : 'None'}
                 </h6>
                 {selectedComponent ? (
                     <div>
                        {Object.keys(editedObjects).map(key => 
                            renderObjectInput(key, editedObjects[key], key)
                        )}
                        <hr style={{borderColor: '#555'}}/>
                        <button className="btn btn-success w-100" onClick={handleObjectSave}>
                            Save Changes
                        </button>
                        <button className="btn btn-outline-secondary w-100 mt-2" onClick={() => deselectComponent()}>
                            Cancel
                        </button>
                     </div>
                 ) : (
                     <p>Select a component to edit.</p>
                 )}
              </SidebarContent>
           </div>
        </div>
      </SidebarContainer>

      <ToggleButton isOpen={isSidebarOpen} onClick={toggleSidebar}>
        {isSidebarOpen ? 'Close' : 'Admin'}
      </ToggleButton>

      {/* Font Settings Modal */}
      <FontSettingsModal
        isOpen={isFontModalOpen}
        onClose={() => setIsFontModalOpen(false)}
      />
    </>
  );
};

AdminSidebar.propTypes = {
  site: PropTypes.object.isRequired,
  page: PropTypes.object.isRequired,
  intl: PropTypes.object,
};

export default injectIntl(AdminSidebar);
